import { c as _c } from "react/compiler-runtime";
import { IconAlertCircleFilled, IconCircleCheckFilled, IconFileSpreadsheet, IconLoader2, IconX } from '@xefi/x-react/icons';
import { TruncatedText } from '@xefi/x-react/typography';
import { mergeTailwindClasses } from '@xefi/x-react/utils';
import { useTranslation } from 'react-i18next';
import type { DownloadItem } from '@/models/download';
import { useMainStore } from '@/store';
import { formatDuration, formatFileSize, getElapsedSeconds } from '@/utils/downloads';
const FULL_PROGRESS_PERCENT = 100;
const STATUS_ICONS: Record<DownloadItem['status'], React.ReactNode> = {
  inProgress: <IconLoader2 className='size-4 shrink-0 animate-spin text-gray-500' />,
  finished: <IconLoader2 className='size-4 shrink-0 animate-spin text-blue-500' />,
  downloaded: <IconCircleCheckFilled className='size-4 shrink-0 text-green-500' />,
  error: <IconAlertCircleFilled className='size-4 shrink-0 text-red-500' />
};
const BAR_COLORS: Record<DownloadItem['status'], string> = {
  inProgress: 'bg-blue-500',
  finished: 'bg-blue-500',
  downloaded: 'bg-green-500',
  error: 'bg-red-500'
};
const getProgressPercent = (download: DownloadItem): number | undefined => {
  if (download.status !== 'inProgress') return FULL_PROGRESS_PERCENT;
  if (!download.totalCount) return undefined;
  return Math.min((download.processedCount ?? 0) / download.totalCount * FULL_PROGRESS_PERCENT, FULL_PROGRESS_PERCENT);
};
type DownloadsPanelItemProps = {
  download: DownloadItem;
  nowTimestamp: number;
};
export const DownloadsPanelItem = t0 => {
  const $ = _c(76);
  const {
    download,
    nowTimestamp
  } = t0;
  const {
    t
  } = useTranslation();
  const removeDownload = useMainStore(_temp);
  let t1;
  if ($[0] !== download) {
    t1 = getProgressPercent(download);
    $[0] = download;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const progressPercent = t1;
  const isIndeterminate = progressPercent === undefined;
  const isActive = download.status === "inProgress" || download.status === "finished";
  let t2;
  if ($[2] !== isActive || $[3] !== t) {
    t2 = isActive ? t("downloadHistory.cancel") : t("downloadHistory.dismiss");
    $[2] = isActive;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const dismissLabel = t2;
  let t3;
  if ($[5] !== download.createdAt || $[6] !== download.estimatedRemainingSeconds || $[7] !== nowTimestamp || $[8] !== t) {
    t3 = download.estimatedRemainingSeconds === undefined ? `${t("downloadHistory.preparing")} ${formatDuration(getElapsedSeconds(download.createdAt, nowTimestamp))}` : t("downloadHistory.remainingTime", {
      duration: formatDuration(download.estimatedRemainingSeconds)
    });
    $[5] = download.createdAt;
    $[6] = download.estimatedRemainingSeconds;
    $[7] = nowTimestamp;
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t) {
    t4 = t("downloadHistory.downloading");
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t) {
    t5 = t("downloadHistory.downloaded");
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t) {
    t6 = t("downloadHistory.failed");
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== t6) {
    t7 = {
      inProgress: t3,
      finished: t4,
      downloaded: t5,
      error: t6
    };
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const statusLabels = t7;
  let t8;
  if ($[21] !== download.processedCount || $[22] !== download.status || $[23] !== download.totalCount || $[24] !== t) {
    t8 = download.totalCount !== undefined && t("downloadHistory.responsesCount", {
      processed: download.status === "inProgress" ? download.processedCount ?? 0 : download.totalCount,
      total: download.totalCount
    });
    $[21] = download.processedCount;
    $[22] = download.status;
    $[23] = download.totalCount;
    $[24] = t;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== download.sizeInBytes) {
    t9 = download.sizeInBytes !== undefined && formatFileSize(download.sizeInBytes);
    $[26] = download.sizeInBytes;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t8 || $[29] !== t9) {
    t10 = [t8, t9].filter(Boolean);
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const rightDetails = t10;
  const t11 = STATUS_ICONS[download.status];
  let t12;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <IconFileSpreadsheet className="size-4 shrink-0 text-green-600" />;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const t13 = download.filename ?? download.surveyTitle;
  let t14;
  if ($[32] !== t13) {
    t14 = <TruncatedText className="truncate text-sm font-medium">{t13}</TruncatedText>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const t15 = `downloadHistory.exportType.${download.exportType}`;
  let t16;
  if ($[34] !== t || $[35] !== t15) {
    t16 = t(t15);
    $[34] = t;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t16) {
    t17 = <p className="text-xs text-gray-500">{t16}</p>;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t14 || $[40] !== t17) {
    t18 = <div className="min-w-0 flex-1">{t14}{t17}</div>;
    $[39] = t14;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== download.id || $[43] !== removeDownload) {
    t19 = () => removeDownload(download.id);
    $[42] = download.id;
    $[43] = removeDownload;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <IconX className="size-4" />;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== dismissLabel || $[47] !== t19) {
    t21 = <button type="button" aria-label={dismissLabel} title={dismissLabel} className="shrink-0 cursor-pointer text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" onClick={t19}>{t20}</button>;
    $[46] = dismissLabel;
    $[47] = t19;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t11 || $[50] !== t18 || $[51] !== t21) {
    t22 = <div className="flex items-center gap-2">{t11}{t12}{t18}{t21}</div>;
    $[49] = t11;
    $[50] = t18;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  const t23 = download.status === "error" && "text-red-500";
  let t24;
  if ($[53] !== t23) {
    t24 = mergeTailwindClasses("text-xs text-gray-500", t23);
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  const t25 = statusLabels[download.status];
  let t26;
  if ($[55] !== t24 || $[56] !== t25) {
    t26 = <span className={t24}>{t25}</span>;
    $[55] = t24;
    $[56] = t25;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== rightDetails) {
    t27 = rightDetails.length > 0 && <span className="shrink-0 text-xs text-gray-500">{rightDetails.join(" \xB7 ")}</span>;
    $[58] = rightDetails;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t26 || $[61] !== t27) {
    t28 = <div className="mt-1.5 flex items-center justify-between gap-2">{t26}{t27}</div>;
    $[60] = t26;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  const t29 = BAR_COLORS[download.status];
  const t30 = isIndeterminate && "w-full animate-pulse";
  let t31;
  if ($[63] !== t29 || $[64] !== t30) {
    t31 = mergeTailwindClasses("h-full rounded-full transition-all", t29, t30);
    $[63] = t29;
    $[64] = t30;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] !== isIndeterminate || $[67] !== progressPercent) {
    t32 = isIndeterminate ? undefined : {
      width: `${progressPercent}%`
    };
    $[66] = isIndeterminate;
    $[67] = progressPercent;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== t31 || $[70] !== t32) {
    t33 = <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"><div className={t31} style={t32} /></div>;
    $[69] = t31;
    $[70] = t32;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t22 || $[73] !== t28 || $[74] !== t33) {
    t34 = <li className="px-3 py-2.5">{t22}{t28}{t33}</li>;
    $[72] = t22;
    $[73] = t28;
    $[74] = t33;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  return t34;
};
function _temp(state) {
  return state.removeDownload;
}